// ===========================================
// Meet & Greet React Query Hooks
// ===========================================
// Custom hooks for meet & greet operations

import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { apiErrorCode, apiErrorMessage } from '../utils/apiError';
import {
  meetAndGreetApi,
  meetAndGreetAdminApi,
  meetAndGreetPublicApi,
  MeetAndGreetFormData,
  MeetAndGreetFilters,
  CreateMeetAndGreetFromEnquiryData,
  AdminCreateMeetAndGreetInput,
  UpdateMeetAndGreetDetailsInput,
  type UpdateMeetAndGreetInput,
  type UpdateMeetAndGreetDetailsResponse,
  type AdminCreateMeetAndGreetResponse,
} from '../api/meetAndGreet.api';
import type { AdminConvertRegistrationFee } from '../api/familyPayments.api';
import { userKeys } from './useUsers';
import { calendarKeys } from './useHybridBooking';
import { dashboardKeys } from './useDashboard';
import { isSpamCheckFailed } from './useTurnstileGate';

// ===========================================
// SHARED HELPERS
// ===========================================

/**
 * Every Meet & Greet write can move the LINKED ENQUIRY's stage (enquiries plan §8.3), and the
 * dashboard counts stages. The list/detail/stats are all under `['enquiries']`. A literal key, not
 * `enquiryKeys`: `useEnquiries` imports this module's keys, so importing back would be a cycle.
 * Mutation responses do not carry the linked enquiry, so the M&G lists refetch too.
 */
export function invalidateLinkedEnquiries(queryClient: QueryClient): void {
  queryClient.invalidateQueries({ queryKey: ['enquiries'] });
  queryClient.invalidateQueries({ queryKey: dashboardKeys.admin.all });
}

/**
 * Every admin Meet & Greet write also moves what the CALENDAR shows (time, teacher, status, the
 * "Notes missing" chip) and the teacher notes caches (#255 /qa L2). ONE helper, so a new mutation
 * cannot forget one. A literal `['mngNotes']`, not `mngNotesKeys`: `useMeetAndGreetNotes` imports
 * this module, so importing back would be a cycle (a test pins the literal to the factory).
 */
export const MNG_NOTES_KEY_ROOT = ['mngNotes'] as const;
export function invalidateAfterMeetAndGreetWrite(queryClient: QueryClient): void {
  queryClient.invalidateQueries({ queryKey: meetAndGreetKeys.all });
  queryClient.invalidateQueries({ queryKey: calendarKeys.all });
  queryClient.invalidateQueries({ queryKey: MNG_NOTES_KEY_ROOT });
}

/**
 * What the office reads when a Meet & Greet write is refused. Switches on `details.code`, never on
 * the message text; any other refusal shows the server's own message.
 */
export function meetAndGreetErrorMessage(error: unknown, fallback: string): string {
  switch (apiErrorCode(error)) {
    case 'MNG_CONVERTED':
      return 'This booking has already been converted to a family, so it can no longer be changed, rejected or cancelled.';
    case 'MNG_FINISHED':
      return 'This booking was cancelled or rejected, so it can no longer be edited.';
    case 'ENQUIRY_ALREADY_LINKED':
      return 'That enquiry already has a Meet & Greet booked.';
    default:
      return apiErrorMessage(error, fallback);
  }
}

// ===========================================
// QUERY KEYS
// ===========================================

export const meetAndGreetKeys = {
  all: ['meetAndGreets'] as const,
  lists: () => [...meetAndGreetKeys.all, 'list'] as const,
  list: (filters?: MeetAndGreetFilters) =>
    [...meetAndGreetKeys.lists(), filters] as const,
  details: () => [...meetAndGreetKeys.all, 'detail'] as const,
  detail: (id: string) => [...meetAndGreetKeys.details(), id] as const,
  counts: () => [...meetAndGreetKeys.all, 'counts'] as const,
  /**
   * Admin scheduling availability. Nested UNDER `all` deliberately, so every
   * existing `invalidateQueries({ queryKey: meetAndGreetKeys.all })` — there
   * are 11 — prefix-matches it. The payload it caches
   * (`getPublicAvailability`) depends on the roster, on existing bookings, on
   * the M&G settings AND on teacher time off; as a root-level literal it was
   * reached by NONE of them, so an admin could schedule a session and still be
   * offered the slot they had just filled.
   */
  adminAvailability: (excludeId?: string) =>
    [...meetAndGreetKeys.all, 'adminAvailability', excludeId] as const,
  /** The Convert dialog's registration-fee choice (online payment + default fee). */
  registrationFee: (id: string) => [...meetAndGreetKeys.all, 'registrationFee', id] as const,
  // Public queries.
  // ⚠️ These three are ROOT-LEVEL on purpose, *while only public pages consume
  // them*: they render in a visitor's browser, so no admin-session invalidation
  // could ever reach them — nesting them would imply a refresh that cannot
  // happen. schoolInfo/schoolInstruments carry no teacher data at all;
  // `availability` does, but its staleness is bounded by staleTime, not by
  // invalidation. ⚠️ That rationale is CONSUMER-DEPENDENT, not permanent: if an
  // admin-session surface ever mounts one of these, re-check whether it should
  // nest under `all` rather than assuming this comment settled it.
  // See md/study/meet-and-greet-availability-keys-2026-09-02.md.
  schoolInfo: (slug: string) => ['schoolInfo', slug] as const,
  schoolInstruments: (slug: string) => ['schoolInstruments', slug] as const,
  availability: (slug: string) => ['meetAndGreetAvailability', slug] as const,
};

// ===========================================
// PUBLIC HOOKS (No Auth)
// ===========================================

/**
 * Get school info by slug (for booking form)
 */
export function useSchoolInfo(schoolSlug: string) {
  return useQuery({
    queryKey: meetAndGreetKeys.schoolInfo(schoolSlug),
    queryFn: () => meetAndGreetPublicApi.getSchoolInfo(schoolSlug),
    enabled: !!schoolSlug,
    retry: false,
  });
}

/**
 * Get instruments for a school (for booking form dropdown)
 */
export function useSchoolInstruments(schoolSlug: string) {
  return useQuery({
    queryKey: meetAndGreetKeys.schoolInstruments(schoolSlug),
    queryFn: () => meetAndGreetPublicApi.getSchoolInstruments(schoolSlug),
    enabled: !!schoolSlug,
  });
}

/**
 * Get availability for meet & greet booking (teachers + time slots)
 */
export function useMeetAndGreetAvailability(schoolSlug: string) {
  return useQuery({
    queryKey: meetAndGreetKeys.availability(schoolSlug),
    queryFn: () => meetAndGreetPublicApi.getAvailability(schoolSlug),
    enabled: !!schoolSlug,
  });
}

/**
 * Get availability for admin scheduling (with option to exclude a booking from
 * conflict check).
 *
 * `enabled` matters more than it looks: both consumers live inside MUI Dialogs,
 * which keep their children MOUNTED when closed, and the parent renders the
 * dialog unconditionally. Without a gate the query stays active all the time,
 * so every one of the 11 `meetAndGreetKeys.all` invalidations would refetch it
 * for a dialog nobody has opened. Callers pass `enabled: false` while closed.
 */
export function useMeetAndGreetAdminAvailability(excludeId?: string, enabled = true) {
  return useQuery({
    queryKey: meetAndGreetKeys.adminAvailability(excludeId),
    queryFn: () => meetAndGreetApi.getAdminAvailability(excludeId),
    enabled,
  });
}

/**
 * Create a new meet & greet booking
 */
export function useCreateMeetAndGreet() {
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (data: MeetAndGreetFormData) =>
      meetAndGreetPublicApi.create(data),
    onSuccess: (data) => {
      enqueueSnackbar(data.message, { variant: 'success' });
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      // #412: the page explains a spam-check refusal inline; no second message here.
      if (isSpamCheckFailed(error)) return;
      const message =
        error.response?.data?.message || 'Failed to create booking';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

/**
 * Verify email address
 */
export function useVerifyMeetAndGreet() {
  return useMutation({
    mutationFn: (token: string) => meetAndGreetPublicApi.verify(token),
  });
}

// ===========================================
// ADMIN HOOKS (Auth Required)
// ===========================================

/**
 * Get all meet & greets for school with optional filters
 */
export function useMeetAndGreets(filters?: MeetAndGreetFilters, enabled = true) {
  return useQuery({
    queryKey: meetAndGreetKeys.list(filters),
    queryFn: () => meetAndGreetApi.getAll(filters),
    enabled,
  });
}

/**
 * Get meet & greet counts by status (for dashboard)
 */
export function useMeetAndGreetCounts(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: meetAndGreetKeys.counts(),
    queryFn: () => meetAndGreetApi.getCounts(),
    enabled: options.enabled ?? true,
  });
}

/**
 * Get single meet & greet by ID
 */
export function useMeetAndGreet(id: string, enabled = true) {
  return useQuery({
    queryKey: meetAndGreetKeys.detail(id),
    queryFn: () => meetAndGreetApi.getById(id),
    enabled: !!id && enabled,
  });
}

/**
 * Update a meet & greet
 */
/**
 * What the admin is told after a details save (#249). Addresses come from the
 * server's `recipients` / `emailFailures`, never from the form.
 */
export function detailsSavedMessage(updated: UpdateMeetAndGreetDetailsResponse): {
  message: string;
  variant: 'success' | 'warning';
} {
  const failures = updated.emailFailures ?? [];
  const recipients = updated.recipients ?? [];
  if (updated.registrationLinkSkippedExistingFamily) {
    return {
      message:
        'Details updated. This email belongs to a family that already has an account, so no registration link was sent. Add the child on the Students page.',
      variant: 'warning',
    };
  }
  if (updated.registrationLinkSendFailed) {
    return {
      message: 'Details updated, but the new registration link could not be sent. Use "Resend registration link" to try again.',
      variant: 'warning',
    };
  }
  if (updated.verificationSendFailed) {
    return {
      message: 'Details updated, but the new verification email could not be sent. Use "Resend verification" to try again.',
      variant: 'warning',
    };
  }
  const kind = updated.registrationLinkSent ? 'registration link' : updated.verificationSent ? 'verification email' : null;
  if (kind) {
    const retry = updated.registrationLinkSent ? 'Resend registration link' : 'Resend verification';
    if (failures.length > 0) {
      return {
        message: `Details updated, but the ${kind} to ${failures.join(', ')} could not be sent. Use "${retry}" to try again.`,
        variant: 'warning',
      };
    }
    return { message: `Details updated and a new ${kind} was sent to ${recipients.join(', ')}.`, variant: 'success' };
  }
  if (updated.registrationLinkRevoked) {
    return { message: 'Details updated. The registration link sent to the old address was cancelled.', variant: 'warning' };
  }
  return { message: 'Details updated', variant: 'success' };
}

/**
 * ADMIN: edit the parent-entered details. Separate from `useUpdateMeetAndGreet`
 * on purpose — different route, different permission. Since #249 a corrected
 * email sends the new registration (or verification) link in the same save, and
 * the server reports exactly what went out; the snackbar repeats it.
 */
export function useUpdateMeetAndGreetDetails() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateMeetAndGreetDetailsInput }) =>
      meetAndGreetApi.updateDetails(id, data),
    onSuccess: (updated) => {
      invalidateAfterMeetAndGreetWrite(queryClient);
      const { message, variant } = detailsSavedMessage(updated);
      enqueueSnackbar(message, { variant, autoHideDuration: variant === 'success' ? 6000 : 10000 });
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      const message = error.response?.data?.message || 'Failed to update details';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

export function useUpdateMeetAndGreet() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    // No `status` here: the PATCH answers 400 to one (mark-verified has its own route).
    mutationFn: ({ id, data }: { id: string; data: UpdateMeetAndGreetInput }) => meetAndGreetApi.update(id, data),
    onSuccess: () => {
      invalidateAfterMeetAndGreetWrite(queryClient);
      // A teacher or time change can move the enquiry (A1 / A1b).
      invalidateLinkedEnquiries(queryClient);
      enqueueSnackbar('Meet & greet updated', { variant: 'success' });
    },
    onError: (error: unknown) => {
      // A converted / finished booking is refused: the list the admin is looking at is stale.
      queryClient.invalidateQueries({ queryKey: meetAndGreetKeys.all });
      enqueueSnackbar(meetAndGreetErrorMessage(error, 'Failed to update'), { variant: 'error' });
    },
  });
}

/**
 * Staff mark an unverified booking as verified (#304). Its own route: the PATCH no longer takes a
 * status. Moves the linked enquiry (BOOKED), so the enquiry caches refresh too.
 */
export function useMarkMeetAndGreetVerified() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (id: string) => meetAndGreetApi.markVerified(id),
    onSuccess: () => {
      invalidateAfterMeetAndGreetWrite(queryClient);
      invalidateLinkedEnquiries(queryClient);
      enqueueSnackbar('Meet & greet marked as verified', { variant: 'success' });
    },
    onError: (error: unknown) => {
      // 400 when it is no longer awaiting verification (the family verified meanwhile): refresh.
      queryClient.invalidateQueries({ queryKey: meetAndGreetKeys.all });
      enqueueSnackbar(meetAndGreetErrorMessage(error, 'Failed to mark as verified'), { variant: 'error' });
    },
  });
}

/**
 * Approve a meet & greet and send registration link
 */
export function useApproveMeetAndGreet() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (id: string) => meetAndGreetApi.approve(id),
    onSuccess: (response) => {
      invalidateAfterMeetAndGreetWrite(queryClient);
      invalidateLinkedEnquiries(queryClient);

      // Approval succeeds even when delivery doesn't. Saying "email sent"
      // regardless would hide a failed send completely — the admin needs to know
      // so they can use Resend.
      // apiClient already unwraps to the payload — there is no nested `.data`.
      const failures = response?.emailFailures ?? [];
      if (failures.length > 0) {
        enqueueSnackbar(
          `Approved, but the registration email could not be delivered to ${failures.join(', ')}. Use "Resend registration link" to try again.`,
          { variant: 'warning', autoHideDuration: 10000 }
        );
        return;
      }

      enqueueSnackbar('Registration email sent to parent', {
        variant: 'success',
      });
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      const message = error.response?.data?.message || 'Failed to approve';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

/**
 * Re-issue and re-send the registration link for an APPROVED booking.
 * The previous link stops working; the 7-day clock restarts.
 */
export function useResendRegistrationLink() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (id: string) => meetAndGreetApi.resendRegistration(id),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: meetAndGreetKeys.all });

      // apiClient already unwraps to the payload — there is no nested `.data`.
      const data = response;
      const failures = data?.emailFailures ?? [];
      const recipients = data?.recipients ?? [];

      // Every send failed: the OLD link is already dead (the token was
      // regenerated before sending), so this is NOT a no-op the admin can retry
      // away — each retry revokes another undelivered token.
      //
      // Deliberately NO toast here. The recovery needs the admin to COPY a URL,
      // and a transient snackbar is the wrong surface for that. The caller opens
      // a persistent dialog from `registrationUrl` instead; see
      // MeetAndGreetPage's manual-link dialog.
      if (recipients.length === 0) {
        return;
      }

      if (failures.length > 0) {
        enqueueSnackbar(
          `Link re-sent to ${recipients.join(', ')}, but delivery failed to ${failures.join(', ')}.`,
          { variant: 'warning', autoHideDuration: 10000 }
        );
        return;
      }

      enqueueSnackbar(`Registration link re-sent to ${recipients.join(', ')}`, {
        variant: 'success',
      });
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      const message =
        error.response?.data?.message || 'Failed to resend registration link';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

/**
 * Re-issue and re-send the email-verification link for an unverified booking.
 */
export function useResendVerificationLink() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (id: string) => meetAndGreetApi.resendVerification(id),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: meetAndGreetKeys.all });

      // apiClient already unwraps to the payload — there is no nested `.data`.
      const data = response;
      const recipients = data?.recipients ?? [];

      if (recipients.length === 0) {
        enqueueSnackbar(
          data?.message ||
            'A new verification link was generated but could not be emailed. The previous link no longer works.',
          { variant: 'error', autoHideDuration: 15000 }
        );
        return;
      }

      // "sent", not "re-sent": the first send of an admin-created booking comes here too (#425 /qa L1).
      enqueueSnackbar(`Verification email sent to ${recipients.join(', ')}`, {
        variant: 'success',
      });
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      const message =
        error.response?.data?.message || 'Failed to resend verification email';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

/**
 * Reject a meet & greet with reason
 */
export function useRejectMeetAndGreet() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      meetAndGreetApi.reject(id, reason),
    onSuccess: () => {
      invalidateAfterMeetAndGreetWrite(queryClient);
      invalidateLinkedEnquiries(queryClient);
      enqueueSnackbar('Meet & greet rejected', { variant: 'info' });
    },
    onError: (error: unknown) => {
      queryClient.invalidateQueries({ queryKey: meetAndGreetKeys.all });
      enqueueSnackbar(meetAndGreetErrorMessage(error, 'Failed to reject'), { variant: 'error' });
    },
  });
}

/**
 * Place a meet & greet on the waitlist
 */
export function useWaitlistMeetAndGreet() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({ id, reason, notifyParent }: { id: string; reason?: string; notifyParent?: boolean }) =>
      meetAndGreetApi.waitlist(id, reason, notifyParent),
    onSuccess: () => {
      invalidateAfterMeetAndGreetWrite(queryClient);
      invalidateLinkedEnquiries(queryClient);
      enqueueSnackbar('Meet & greet added to waitlist', { variant: 'info' });
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      const message = error.response?.data?.message || 'Failed to waitlist';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

/**
 * Admin convert: create family/student accounts and record the
 * registration-fee outcome the admin chose, in one transaction.
 */
export function useAdminConvertMeetAndGreet() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({
      id,
      registrationFee,
      confirmNewFamily,
    }: {
      id: string;
      registrationFee: AdminConvertRegistrationFee;
      confirmNewFamily?: boolean;
    }) =>
      // The API layer omits the key unless it is true (#430 review N4).
      meetAndGreetApi.adminConvert(id, registrationFee, confirmNewFamily || undefined),
    onSuccess: (data) => {
      invalidateAfterMeetAndGreetWrite(queryClient);
      queryClient.invalidateQueries({ queryKey: userKeys.parents() });
      queryClient.invalidateQueries({ queryKey: userKeys.students() });
      // CONVERTED links the family (and the student) to the enquiry and moves it to Onboarding.
      invalidateLinkedEnquiries(queryClient);
      enqueueSnackbar(data.message || 'Family and student accounts created successfully', { variant: 'success' });
    },
    onError: (_error: unknown, variables) => {
      // A 409 (e.g. "already paid online") means the fee data the dialog
      // showed is now stale — refetch it so the dialog re-renders in the
      // correct state rather than repeating a choice that will 409 again.
      queryClient.invalidateQueries({ queryKey: meetAndGreetKeys.registrationFee(variables.id) });
      // No error snackbar: the only caller (ConvertToStudentDialog) shows the
      // server's message inline and stays open — a toast would say it twice.
    },
  });
}

/**
 * The Convert dialog's registration-fee choice: any existing online payment,
 * plus the school's default fee to prefill "Paid another way".
 */
export function useMeetAndGreetRegistrationFee(id: string | null) {
  return useQuery({
    queryKey: meetAndGreetKeys.registrationFee(id ?? ''),
    queryFn: () => meetAndGreetApi.getRegistrationFee(id as string),
    enabled: !!id,
  });
}

/**
 * Cancel a meet & greet
 */
export function useCancelMeetAndGreet() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (id: string) => meetAndGreetApi.cancel(id),
    onSuccess: () => {
      invalidateAfterMeetAndGreetWrite(queryClient);
      invalidateLinkedEnquiries(queryClient);
      enqueueSnackbar('Meet & greet cancelled', { variant: 'info' });
    },
    onError: (error: unknown) => {
      queryClient.invalidateQueries({ queryKey: meetAndGreetKeys.all });
      enqueueSnackbar(meetAndGreetErrorMessage(error, 'Failed to cancel'), { variant: 'error' });
    },
  });
}

// ===========================================
// SETTINGS HOOKS
// ===========================================

/**
 * Get meet & greet settings
 */
export function useMeetAndGreetSettings() {
  return useQuery({
    queryKey: ['meetAndGreetSettings'],
    queryFn: () => meetAndGreetApi.getSettings(),
  });
}

/**
 * Update meet & greet settings
 */
export function useUpdateMeetAndGreetSettings() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (data: {
      sessionDuration?: number;
      bufferTime?: number;
      advanceBookingDays?: number;
      minimumNoticeHours?: number;
    }) => meetAndGreetApi.updateSettings(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['meetAndGreetSettings'] });
      enqueueSnackbar('Settings updated', { variant: 'success' });
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      const message = error.response?.data?.message || 'Failed to update settings';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

/**
 * Add a time slot
 */
export function useAddMeetAndGreetTimeSlot() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (data: {
      dayOfWeek: number;
      startTime: string;
      endTime: string;
      isActive?: boolean;
    }) => meetAndGreetApi.addTimeSlot(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['meetAndGreetSettings'] });
      enqueueSnackbar('Time slot added', { variant: 'success' });
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      const message = error.response?.data?.message || 'Failed to add time slot';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

/**
 * Create a meet & greet from an enquiry
 */
export function useCreateMeetAndGreetFromEnquiry() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (data: CreateMeetAndGreetFromEnquiryData) =>
      meetAndGreetAdminApi.createFromEnquiry(data),
    onSuccess: () => {
      invalidateAfterMeetAndGreetWrite(queryClient);
      queryClient.invalidateQueries({ queryKey: ['enquiries'] });
      // The create moves the enquiry's stage (A1), which the admin dashboard counts (review 5.7 M2).
      queryClient.invalidateQueries({ queryKey: dashboardKeys.admin.all });
      enqueueSnackbar('Meet & Greet booking created from enquiry', { variant: 'success' });
    },
    onError: (error: unknown) => {
      // A 409 ENQUIRY_ALREADY_LINKED means the panel was stale: refresh it.
      queryClient.invalidateQueries({ queryKey: ['enquiries'] });
      enqueueSnackbar(meetAndGreetErrorMessage(error, 'Failed to create Meet & Greet booking'), { variant: 'error' });
    },
  });
}

/**
 * What the admin is told after creating a Meet & Greet (#425). Addresses come from
 * the server (`recipients` / `emailFailures`), never the form; every field is
 * defaulted, so a bare row is a plain success (/qa M2).
 */
export function createdMessage(created: Partial<AdminCreateMeetAndGreetResponse> | undefined): {
  message: string;
  variant: 'success' | 'warning';
} {
  const recipients = created?.recipients ?? [];
  const failures = created?.emailFailures ?? [];
  if (created?.registrationLinkSendFailed) {
    return {
      message: 'Meet & Greet created, but the registration link could not be sent. Press Approve on the Meet & Greet page to send it.',
      variant: 'warning',
    };
  }
  const kind = created?.registrationLinkSent
    ? 'registration link'
    : created?.verificationSent
      ? 'verification email'
      : null;
  if (!kind) return { message: 'Meet & Greet created', variant: 'success' };
  if (failures.length > 0) {
    const retry = created?.registrationLinkSent ? 'Resend registration link' : 'Resend verification';
    return {
      message: `Meet & Greet created, but the ${kind} to ${failures.join(', ')} could not be sent. Use "${retry}" to try again.`,
      variant: 'warning',
    };
  }
  return { message: `Meet & Greet created and the ${kind} was sent to ${recipients.join(", ")}.`, variant: 'success' };
}

/**
 * Admin creates a Meet & Greet and picks how far it goes (#425). The server reports
 * what it sent; the snackbar repeats it (`createdMessage`).
 * Used by the admin Calendar toolbar and the Meet & Greet page header.
 */
export function useAdminCreateMeetAndGreet() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (data: AdminCreateMeetAndGreetInput) =>
      meetAndGreetAdminApi.adminCreate(data),
    onSuccess: (created) => {
      invalidateAfterMeetAndGreetWrite(queryClient);
      // With an `enquiryId` the create links and moves that enquiry.
      invalidateLinkedEnquiries(queryClient);
      const { message, variant } = createdMessage(created);
      enqueueSnackbar(message, { variant, autoHideDuration: variant === 'success' ? 6000 : 10000 });
    },
    onError: (error: unknown) => {
      enqueueSnackbar(meetAndGreetErrorMessage(error, 'Failed to create Meet & Greet'), { variant: 'error' });
    },
  });
}

/**
 * Delete a time slot
 */
export function useDeleteMeetAndGreetTimeSlot() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (slotId: string) => meetAndGreetApi.deleteTimeSlot(slotId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['meetAndGreetSettings'] });
      enqueueSnackbar('Time slot deleted', { variant: 'success' });
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      const message = error.response?.data?.message || 'Failed to delete time slot';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}
